// api.ts (fixed)
import toast from 'react-hot-toast';
import type { RecordModel } from 'pocketbase';
import pb from '@libs/pocketbase';
import http from '@libs/http';
import { ROLES } from '@config/constants';
import type {
  CustomerRecord,
  CustomerFilters,
  CustomerUpdateValues,
  CustomerCreateValues,
  SubscriptionRecord,
  CustomerTypeRecord,
} from './types';

const collection = () => pb.collection('customers');
const subscriptionsCollection = () => pb.collection('subscriptions');
const usersCollection = () => pb.collection('users');
const customerTypesCollection = () => pb.collection('customer_types');
const MAIL_ENDPOINT = '/api/mail/customer-account';

/* ----------------------- SANITIZE FILTER (SIMPLIFIED) ----------------------- */
// ⚠️ QUAN TRỌNG: Đơn giản hóa filter để tránh URL quá dài
const sanitizeFilter = (filters?: CustomerFilters): string => {
  const clauses: string[] = ['is_deleted!=true'];

  if (filters?.search) {
    // Chỉ search theo name (tránh quá nhiều field)
    const escaped = filters.search.replace(/"/g, '\\"');
    clauses.push(`name~"${escaped}"`);
  }

  if (filters?.status && filters.status !== 'all') {
    clauses.push(`status="${filters.status}"`);
  }

  // Không search customer_type_id ở đây để tránh URL dài
  return clauses.join('&&');
};

/* ----------------------- MAP RECORD ----------------------- */
type CustomerRecordModel = RecordModel & {
  name: string;
  representative_name?: string;
  representative_phone?: string;
  email?: string;
  phone?: string;
  address?: string;
  tax_code?: string;
  logo?: string;
  status?: 'active' | 'inactive';
  customer_type_id?: string;
  source?: string;
  notes?: string;
  is_deleted?: boolean;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
  expand?: {
    customer_type_id?: CustomerTypeRecord;
  };
};

const mapRecord = (record: RecordModel): CustomerRecord => {
  const r = record as CustomerRecordModel;
  
  // Convert logo filename to full URL if it exists
  let logoUrl: string | undefined = r.logo;
  if (logoUrl && !logoUrl.startsWith('http') && !logoUrl.startsWith('/')) {
    // If logo is a filename (not a full URL), construct the full URL
    logoUrl = `${pb.baseUrl}/api/files/${collection().collectionIdOrName}/${record.id}/${logoUrl}`;
  }
  
  return {
    id: record.id,
    name: r.name,
    representative_name: r.representative_name,
    representative_phone: r.representative_phone,
    email: r.email,
    phone: r.phone,
    address: r.address,
    tax_code: r.tax_code,
    logo: logoUrl,
    status: r.status ?? 'active',
    customer_type_id: r.customer_type_id,
    source: r.source,
    notes: r.notes,
    is_deleted: r.is_deleted ?? false,
    created_by: r.created_by,
    updated_by: r.updated_by,
    created_at: r.created_at ?? '',
    updated_at: r.updated_at ?? '',
    customer_type: r.expand?.customer_type_id,
    subscriptions: [],
  };
};

/* ----------------------- GET CUSTOMERS WITH SUBSCRIPTIONS (OPTIMIZED) ----------------------- */
// 🔧 Lấy tất cả customers (có subscription hoặc không), sau đó merge subscriptions
const getCustomersWithSubscriptions = async (
  filter?: string
): Promise<CustomerRecord[]> => {
  try {
    // 1️⃣ Lấy tất cả customers theo filter (không phụ thuộc subscription)
    const allCustomers: RecordModel[] = [];
    const batchSize = 25;
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const result = await collection().getList(page, batchSize, {
          filter: filter || 'is_deleted!=true',
          expand: 'customer_type_id',
          sort: '-created_at',
        });
        allCustomers.push(...result.items);
        hasMore = result.items.length === batchSize;
        page++;
      } catch (err) {
        console.warn(`Page ${page} failed:`, err);
        hasMore = false;
      }
    }

    if (allCustomers.length === 0) return [];

    // 2️⃣ Lấy tất cả subscriptions và group theo customer_id
    const subscriptions = await subscriptionsCollection().getFullList<
      SubscriptionRecord
    >({
      filter: 'status!="cancelled"',
      limit: 500,
    });

    const subsByCustomer = new Map<string, SubscriptionRecord[]>();
    subscriptions.forEach((sub) => {
      if (!subsByCustomer.has(sub.customer_id)) {
        subsByCustomer.set(sub.customer_id, []);
      }
      subsByCustomer.get(sub.customer_id)!.push(sub);
    });

    // 3️⃣ Deduplicate customers (đảm bảo mỗi customer chỉ xuất hiện 1 lần)
    const uniqueCustomersMap = new Map<string, RecordModel>();
    allCustomers.forEach((item) => {
      if (!uniqueCustomersMap.has(item.id)) {
        uniqueCustomersMap.set(item.id, item);
      }
    });

    // 4️⃣ Map & merge subscriptions vào customers
    return Array.from(uniqueCustomersMap.values()).map((item) => {
      const mapped = mapRecord(item);
      mapped.subscriptions = subsByCustomer.get(item.id) ?? [];
      return mapped;
    });
  } catch (error) {
    console.error('getCustomersWithSubscriptions error:', error);
    return [];
  }
};

/* ----------------------- API EXPORT ----------------------- */
export const customerApi = {
  async list(filters?: CustomerFilters): Promise<CustomerRecord[]> {
    const filter = sanitizeFilter(filters);
    return getCustomersWithSubscriptions(filter);
  },

  async summary(filters?: CustomerFilters) {
    const records = await this.list(filters);
    const total = records.length;
    const active = records.filter((r) => r.status === 'active').length;
    return { total, active, inactive: total - active };
  },

  async get(id: string): Promise<CustomerRecord> {
    try {
      const record = await collection().getOne<RecordModel>(id, {
        expand: 'customer_type_id',
      });

      const mapped = mapRecord(record);

      // Load subscriptions
      const subs = await subscriptionsCollection().getFullList<
        SubscriptionRecord
      >({
        filter: `customer_id="${id}"`,
      });

      mapped.subscriptions = subs;
      return mapped;
    } catch (error) {
      console.error('Failed to get customer:', error);
      throw error;
    }
  },

  async create(payload: CustomerCreateValues) {
    const trim = (v?: string) => (typeof v === 'string' ? v.trim() : undefined);

    // Validate email format
    const normalizedEmail = payload.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      toast.error('Email không hợp lệ');
      throw new Error('Email không hợp lệ');
    }

    // Validate password length
    const password = payload.password.trim();
    if (password.length < 8) {
      toast.error('Mật khẩu phải có ít nhất 8 ký tự');
      throw new Error('Mật khẩu phải có ít nhất 8 ký tự');
    }

    // Validate password match
    if (password !== payload.passwordConfirm.trim()) {
      toast.error('Mật khẩu xác nhận không khớp');
      throw new Error('Mật khẩu xác nhận không khớp');
    }

    // Check if email already exists in users
    let existingUser = null;
    try {
      const usersByEmail = await usersCollection().getList(1, 1, {
        filter: `email="${normalizedEmail}"`,
        requestKey: `check-user-email-${Date.now()}`,
      });
      if (usersByEmail.items && usersByEmail.items.length > 0) {
        existingUser = usersByEmail.items[0];
      }
    } catch (error) {
      // Ignore, continue
    }

    if (existingUser) {
      toast.error('Email đã được sử dụng bởi tài khoản khác');
      throw new Error('Email đã được sử dụng bởi tài khoản khác');
    }

    // Check if customer with this email already exists
    let existingCustomer = null;
    try {
      const customersByEmail = await collection().getList(1, 1, {
        filter: `email="${normalizedEmail}"&&is_deleted!=true`,
        requestKey: `check-customer-email-${normalizedEmail}`,
      });
      if (customersByEmail.items && customersByEmail.items.length > 0) {
        existingCustomer = customersByEmail.items[0];
      }
    } catch (error) {
      // Ignore, continue
    }

    if (existingCustomer) {
      toast.error('Email đã được sử dụng bởi khách hàng khác');
      throw new Error('Email đã được sử dụng bởi khách hàng khác');
    }

    // Create customer record
    const customerBody: Record<string, unknown> = {
      name: trim(payload.name),
      representative_name: trim(payload.representative_name),
      representative_phone: trim(payload.representative_phone),
      email: normalizedEmail,
      phone: trim(payload.phone),
      address: trim(payload.address),
      tax_code: trim(payload.tax_code),
      status: payload.status ?? 'active',
      customer_type_id: payload.customer_type_id || undefined,
      source: trim(payload.source),
      notes: trim(payload.notes),
    };

    // Handle logo: if it's a File, use FormData; if it's a string URL, use it directly
    const isLogoFile = payload.logo instanceof File;
    let formData: FormData | null = null;

    if (isLogoFile && payload.logo instanceof File) {
      // Create FormData for file upload
      // IMPORTANT: Field logo must be type "file" in PocketBase schema, not "url"
      // If field is type "url", change it to "file" in PocketBase Admin UI
      formData = new FormData();
      formData.append('logo', payload.logo);
      
      // Add other fields to FormData
      Object.keys(customerBody).forEach((key) => {
        const value = customerBody[key];
        if (value !== undefined && value !== null && value !== '') {
          if (value instanceof File) {
            formData!.append(key, value);
          } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            formData!.append(key, String(value));
          }
        }
      });
    } else if (payload.logo && typeof payload.logo === 'string' && payload.logo.trim()) {
      customerBody.logo = trim(payload.logo);
    }

    Object.keys(customerBody).forEach((k) => {
      if (!customerBody[k]) delete customerBody[k];
    });

    let createdCustomer: RecordModel;
    try {
      // Use FormData if logo is a File, otherwise use regular object
      if (formData) {
        createdCustomer = await collection().create(formData);
      } else {
        createdCustomer = await collection().create(customerBody);
      }
      
      // After creation, construct logo URL if it was uploaded
      if (isLogoFile && createdCustomer.logo) {
        const logoField = createdCustomer.logo as string;
        if (logoField && !logoField.startsWith('http')) {
          // Construct full URL: /api/files/{collection}/{recordId}/{filename}
          const logoUrl = `${pb.baseUrl}/api/files/${collection().collectionIdOrName}/${createdCustomer.id}/${logoField}`;
          console.log('Logo uploaded successfully:', logoUrl);
        }
      }
    } catch (error) {
      toast.error('Lỗi tạo khách hàng');
      throw error;
    }

    // Create user account
    let createdUser: RecordModel | null = null;
    try {
      const userName = trim(payload.representative_name) || trim(payload.name);
      const userPayload: Record<string, unknown> = {
        email: normalizedEmail,
        password: password,
        passwordConfirm: password,
        role: ROLES.ADMIN_COMPANY,
        company_id: createdCustomer.id,
      };

      if (userName) {
        userPayload.name = userName;
      }

      createdUser = await usersCollection().create(userPayload);
      if (createdUser) {
        console.log('User created successfully:', createdUser.id);
      }

      // Verify user
      if (createdUser) {
        try {
          await usersCollection().update(createdUser.id, {
            verified: true,
          });
          console.log('User verified successfully');
        } catch (verifyError) {
          console.warn('Could not verify user immediately:', verifyError);
        }
      }
    } catch (error) {
      console.error('Create user error:', error);
      
      // Rollback: Delete customer if user creation failed
      try {
        await collection().delete(createdCustomer.id);
        console.log('Rollback: Deleted customer due to user creation failure');
      } catch (deleteError) {
        console.error('Rollback failed:', deleteError);
      }

      const errorMessage = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Không thể tạo tài khoản người dùng';

      if (errorMessage.toLowerCase().includes('validation_values_mismatch') ||
          errorMessage.toLowerCase().includes("values don't match")) {
        toast.error('Lỗi validation dữ liệu. Vui lòng kiểm tra lại thông tin đã nhập.');
        throw new Error('Lỗi validation dữ liệu');
      }

      if (errorMessage.toLowerCase().includes('permission') || errorMessage.toLowerCase().includes('rule')) {
        toast.error('Không có quyền tạo tài khoản. Vui lòng đảm bảo đã đăng nhập với tài khoản Super Admin.');
        throw new Error('Không có quyền tạo tài khoản');
      }

      toast.error('Lỗi tạo tài khoản người dùng');
      throw new Error(errorMessage);
    }

    // Send email notification (only if sendInvite is true)
    // Email failure should not block the success flow
    if (createdUser && payload.sendInvite !== false) {
      try {
        const loginUrl =
          typeof window !== 'undefined' && window.location
            ? `${window.location.origin}/login`
            : '/login';

        await http.post(MAIL_ENDPOINT, {
          email: normalizedEmail,
          customer_name: trim(payload.name) || '',
          username: normalizedEmail, // Use email as username
          password: password,
          login_url: loginUrl,
        });
        console.log('Email sent successfully');
      } catch (emailError) {
        // Log warning but don't show error toast - email failure is not critical
        console.warn('Email service not available:', {
          endpoint: MAIL_ENDPOINT,
          error: emailError instanceof Error ? emailError.message : 'Unknown error',
          status: emailError && typeof emailError === 'object' && 'status' in emailError 
            ? emailError.status 
            : 'unknown',
        });
        
        // Log thông tin để admin có thể gửi email thủ công
        console.info('Customer account information (send manually if needed):', {
          email: normalizedEmail,
          customer_name: trim(payload.name),
          username: normalizedEmail,
          password: password,
          login_url: typeof window !== 'undefined' && window.location
            ? `${window.location.origin}/login`
            : '/login',
        });
        // Don't show error toast - email is optional
      }
    }

    // Single success message
    toast.success('Tạo tài khoản thành công');

    return mapRecord(createdCustomer);
  },

  async update(id: string, payload: CustomerUpdateValues) {
    try {
      const isLogoFile = payload.logo instanceof File;
      let formData: FormData | null = null;
      const updateBody: Record<string, unknown> = { ...payload };
      
      // Handle logo: if it's a File, use FormData; if it's a string URL, use it directly
      if (payload.logo !== undefined) {
        if (isLogoFile && payload.logo instanceof File) {
          // Create FormData for file upload
          formData = new FormData();
          formData.append('logo', payload.logo);
          
          // Add other fields to FormData
          Object.keys(updateBody).forEach((key) => {
            if (key !== 'logo') {
              const value = updateBody[key];
              if (value !== undefined && value !== null && value !== '') {
                if (value instanceof File) {
                  formData!.append(key, value);
                } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                  formData!.append(key, String(value));
                }
              }
            }
          });
        } else if (typeof payload.logo === 'string') {
          const trimmed = payload.logo.trim();
          if (trimmed) {
            updateBody.logo = trimmed;
          } else {
            // Empty string means remove logo
            updateBody.logo = '';
          }
        }
      }

      // Use FormData if logo is a File, otherwise use regular object
      const record = isLogoFile && formData
        ? await collection().update(id, formData)
        : await collection().update(id, updateBody);
      
      toast.success('Cập nhật thông tin khách hàng thành công');
      return mapRecord(record);
    } catch (error) {
      toast.error('Lỗi cập nhật khách hàng');
      throw error;
    }
  },

  async toggleStatus(id: string, nextStatus: 'active' | 'inactive') {
    try {
      const record = await collection().update(id, {
        status: nextStatus,
      });
      toast.success(
        nextStatus === 'active' ? 'Đã kích hoạt khách hàng' : 'Đã vô hiệu hóa khách hàng'
      );
      return mapRecord(record);
    } catch (error) {
      toast.error('Lỗi cập nhật trạng thái');
      throw error;
    }
  },

  async resetPassword(customerId: string) {
    try {
      const users = await pb.collection('users').getFullList({
        filter: `company_id="${customerId}"&&role="company_admin"`,
        limit: 1,
      });

      if (users.length === 0) {
        toast.error('Không tìm thấy Admin Công Ty');
        return;
      }

      const email = (users[0] as { email?: string }).email;
      if (!email) {
        toast.error('Admin Công Ty không có email');
        return;
      }

      await pb.collection('users').requestPasswordReset(email);
      toast.success('Đã gửi email đặt lại mật khẩu');
    } catch (error) {
      toast.error('Lỗi reset mật khẩu');
      console.error(error);
    }
  },

  async remove(id: string) {
    try {
      const record = await collection().update(id, {
        is_deleted: true,
        status: 'inactive',
      });
      toast.success('Đã xóa khách hàng (soft delete)');
      return mapRecord(record);
    } catch (error) {
      toast.error('Lỗi xóa khách hàng');
      throw error;
    }
  },

  async getCustomerTypes(): Promise<CustomerTypeRecord[]> {
    try {
      const records = await customerTypesCollection().getFullList<CustomerTypeRecord>({
        sort: 'name',
      });
      return records;
    } catch (error) {
      console.error('Failed to get customer types:', error);
      return [];
    }
  },
};